import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, assignTask, fetchMembers, fetchRoleAssignments, fetchTask } from '../api/client';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { createIdempotencyKeyHolder } from '../lib/idempotency';
import { kurirTersedia } from '../lib/worker-candidates';
import { formatTaskStatus, isTransitionLegal, type FieldTaskStatus } from '../lib/task-actions';
import { ErrorCard, LoadingState } from '../states';

/**
 * Tugaskan / alihkan tugas — PILIH NAMA, bukan ketik UUID.
 *
 * Layar ini dulu meminta "User ID pelaksana" berupa UUID yang diketik tangan, dengan alasan
 * bahwa pemilih setengah jadi lebih menyesatkan daripada isian yang jujur menuntut identitas
 * persis. Alasan itu keliru di lapangan: isian yang jujur pun tidak berguna kalau TIDAK SEORANG
 * PUN dapat mengisinya. Owner menemukannya dengan satu pertanyaan — "bagaimana isi petugas yang
 * ditugaskan?" (26 Agustus 2026) — dan pertanyaan itu adalah jawabannya.
 *
 * Daftarnya diambil dari keanggotaan organisasi, dan yang tidak aktif disaring keluar. Bila
 * daftarnya kosong, itu dikatakan apa adanya — bukan disamarkan menjadi isian kosong yang tampak
 * seperti kesalahan pemakai.
 */
export function TaskAssignPage(): ReactNode {
  const session = useSession();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const keys = useMemo(() => createIdempotencyKeyHolder(), []);

  const [sending, setSending] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  /*
   * Tugas DAN daftar orang dalam satu panggilan. Menariknya terpisah setelah tugas dimuat berarti
   * hook bersyarat — cacat yang menjatuhkan halaman detail hari ini (React #310).
   */
  const { state } = useAsync(async () => {
    const [task, members, roles] = await Promise.all([
      fetchTask(session.auth, id),
      fetchMembers(session.auth).catch(() => []),
      fetchRoleAssignments(session.auth).catch(() => []),
    ]);
    return { task, kurir: kurirTersedia(members, roles) };
  }, [session.auth, id]);

  if (state.status === 'loading') return <LoadingState label="Memuat tugas…" />;
  if (state.status === 'error') return <ErrorCard error={state.error} />;

  const { task, kurir } = state.data;
  /* Yang tidak aktif tidak boleh menerima tugas baru; server menolaknya (TASK-004) dan
     menampilkannya di sini hanya menghasilkan penolakan yang bisa dicegah lebih awal. */
  const orang = kurir.filter((m) => m.userId !== task.assigneeUserId);
  // Setelah `started`, pengalihan ditolak `TASK-008`. Dikatakan di sini, bukan dibiarkan
  // menjadi kejutan setelah dispatcher mengetik UUID.
  const canAssign = isTransitionLegal(task.status as FieldTaskStatus, 'assign');

  async function tugaskan(userId: string): Promise<void> {
    setError(null);
    setSending(userId);
    try {
      await assignTask(session.auth, task.id, keys.current(), userId);
      keys.rotate();
      navigate(`/tasks/${task.id}`);
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError(0, 'UNKNOWN', 'Kesalahan tidak dikenal.', null),
      );
      setSending(null);
    }
  }

  return (
    <>
      <section className="card">
        <Link to={`/tasks/${task.id}`}>← Detail tugas</Link>
        <h2>Tugaskan: {task.title}</h2>
        <p className="muted small">
          {task.taskNumber} · status {formatTaskStatus(task.status)}
          {task.assigneeUserId ? ' · sudah punya pelaksana' : ''}
        </p>
      </section>

      {!canAssign ? (
        <section className="card" role="status">
          <p className="warning">Tugas ini tidak dapat dialihkan lagi.</p>
          <p className="muted small">
            Pengalihan hanya bisa sebelum pelaksana mulai bekerja. Batalkan tugas ini lalu buat yang
            baru bila pekerjaannya harus berpindah orang.
          </p>
        </section>
      ) : (
        <section className="card">
          <h3 className="small">{task.assigneeUserId ? 'Alihkan kepada' : 'Berikan kepada'}</h3>
          {orang.length === 0 ? (
            <p className="warning small">
              Tidak ada kurir lain yang dapat dipilih. Beri peran Kurir lewat layar Karyawan lebih
              dulu.
            </p>
          ) : (
            <ul className="list">
              {orang.map((m) => (
                <li key={m.userId} className="list-item">
                  <button
                    type="button"
                    className="link"
                    disabled={sending !== null}
                    onClick={() => void tugaskan(m.userId)}
                  >
                    {sending === m.userId ? 'Menugaskan…' : m.displayName}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {error ? <ErrorCard error={error} /> : null}
    </>
  );
}
